import { v4 as uuid } from 'uuid';

import { MESSAGES, ROLE } from '../../../constants';

export type ArticleVideo = Readonly<{
  url: string;
  duration?: string;
  durationSeconds?: number;
  title?: string;
}>;

export type IAIArticleAnswerItem = Readonly<{
  articleId: string;
  title: string;
  url: string;
  description?: string;
  videos?: ArticleVideo[];
}>;

const formatDuration = (seconds: unknown): string | undefined => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
    return undefined;
  }
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
};

const toDurationSeconds = (seconds: unknown): number | undefined =>
  typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : undefined;

const normalizeVideo = (video): ArticleVideo => ({
  url: video.url,
  duration: formatDuration(video.duration),
  durationSeconds: toDurationSeconds(video.duration),
  title: video.title,
});

export interface IAIArticleAnswer {
  readonly id: string;
  readonly nodeId: string;
  readonly type: MESSAGES.AI_ARTICLE_ANSWER;
  readonly answer: string;
  readonly articles: IAIArticleAnswerItem[];
  readonly rm: boolean;
  readonly role: ROLE;
  readonly preventScroll: boolean;
  readonly isStreaming?: boolean;
  readonly isCancelled?: boolean;
}

// max number of articles we show under this message when the videos feature is off
export const ARTICLES_LIMIT = 3;

export const createAIArticleAnswer = (data): IAIArticleAnswer => {
  const articles = data.articles.map((article) => ({
    articleId: article.articleId,
    title: article.title,
    url: article.url,
    description: article.description,
    videos: (article.articleVideos ?? []).map(normalizeVideo),
  }));

  return {
    id: data.id || uuid(),
    nodeId: data.nodeId,
    type: MESSAGES.AI_ARTICLE_ANSWER,
    answer: data.answer,
    articles,
    rm: Boolean(data.rm),
    preventScroll: Boolean(data.preventScroll),
    role: ROLE.BOT,
  };
};
